import { FormControl, FormGroup, Validators } from '@angular/forms';

import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';

import { FacetRangeFilter, FacetRangeForm } from '../types';

export class DpFacetRangeForm extends FormGroup<FacetRangeForm> {
  #range: FacetRangeFilter;

  constructor(range: FacetRangeFilter) {
    super({
      from: new FormControl<number>(range.from, { nonNullable: true }),
      to: new FormControl<number>(range.to, { nonNullable: true })
    });
    this.#range = range;
    this.#updateFormState(range);
  }

  get from(): FormControl<number> {
    return this.controls.from;
  }

  get to(): FormControl<number> {
    return this.controls.to;
  }

  get fromControlValueChanges(): Observable<number> {
    return this.controls.from.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap((value) => {
        if (this.from.invalid) {
          this.from.setValue(Math.min(Math.max(value, this.#range.from), this.to.value ?? Infinity));
        }
        this.#updateControlValidators(
          this.to,
          this.#roundValue(this.from.value ?? this.#range.from),
          this.#range.to
        );
      })
    );
  }

  get toControlValueChanges(): Observable<number> {
    return this.to.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap((value) => {
        if (this.to.invalid) {
          this.to.setValue(Math.max(Math.min(value, this.#range.to), this.from.value ?? -Infinity));
        }
        this.#updateControlValidators(
          this.from,
          this.#range.from,
          this.#roundValue(this.to.value ?? this.#range.to)
        );
      })
    );
  }

  updateForm(initialState: FacetRangeFilter, range: FacetRangeFilter): void {
    this.setValue({ from: initialState.from, to: initialState.to });
    this.#range = range;
    this.#updateFormState(range);
  }

  #updateFormState(range: FacetRangeFilter): void {
    if (range.from === range.to) {
      this.disable();
    } else {
      this.enable();
      this.#updateControlValidators(this.from, range.from, range.to);
      this.#updateControlValidators(this.to, range.from, range.to);
    }
  }

  #roundValue(value: number): number {
    return Math.round(value * 100) / 100;
  }

  #updateControlValidators(control: FormControl, min = 0, max = 0): void {
    control.setValidators([Validators.min(min), Validators.max(max)]);
  }
}
